import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, IonModal, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { BerService } from '../ber.service';

type Nezet = 'jogcim' | 'munkaszam' | 'nyugdijpenztar';

/**
 *  BÉR TÖRZSEK – az RLB Törzsadatok menüjének párja: Jogcím törzs, Munkaszám törzs,
 *  Nyugdíjpénztár törzs (backend/ber-torzs.js). A nézetet az útvonal adja (route data).
 *   - Jogcímek: a beépített (közös) sorok csak olvashatók – a motor saját tételei (alapbér,
 *     pótlékok, távolléti díj) és a gyakori egyéb jogcímek –, sajátot bármikor fel lehet venni.
 *   - Munkaszámok: ügyfelenként; a jogviszonyhoz rendelhető (bérfeladás / költséghely).
 *   - Nyugdíjpénztárak: intézményenként; a tagság az alkalmazottnál rögzíthető.
 */
@Component({
  selector: 'app-ber-torzsek',
  templateUrl: './torzsek.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss'],
})
export class BerTorzsekComponent implements OnInit, OnDestroy {
  @ViewChild('szerkModal') szerkModal: IonModal;
  nezet: Nezet = 'jogcim';
  sorok: any[] = [];
  szerk: any = null;
  szuro = '';
  /** Fut-e az MNB-frissítés (nyugdíjpénztár-törzs). */
  mnbFut = false;
  private sub?: Subscription;

  constructor(private ber: BerService, private route: ActivatedRoute, private toast: ToastController, private alert: AlertController) {}

  get aktiv() { return this.ber.aktiv; }
  get cim(): string { return { jogcim: 'Jogcím törzs', munkaszam: 'Munkaszám törzs', nyugdijpenztar: 'Nyugdíjpénztár törzs' }[this.nezet]; }
  get alcim(): string {
    return {
      jogcim: 'Bérelemek és levonások – az adózási jelzők döntik el, melyik alapba tartoznak',
      munkaszam: (this.aktiv.ugyfelNev || 'nincs kiválasztott ügyfél') + ' · költséghelyek, munkaszámok',
      nyugdijpenztar: 'Önkéntes és magánnyugdíjpénztárak – a tagság az alkalmazottnál rögzíthető',
    }[this.nezet];
  }
  get szurt(): any[] {
    const s = this.szuro.trim().toLowerCase();
    return s ? this.sorok.filter((r) => `${r.kod || ''} ${r.nev || ''}`.toLowerCase().includes(s)) : this.sorok;
  }

  async ngOnInit() {
    await this.ber.init();
    this.sub = this.route.data.subscribe(async (d) => { this.nezet = (d['nezet'] as Nezet) || 'jogcim'; this.szuro = ''; await this.betolt(); });
  }
  ngOnDestroy() { this.sub?.unsubscribe(); }

  private api(ut: string) { return this.ber.torzsApi.get<any[]>(ut); }

  async betolt() {
    this.sorok = [];
    try {
      if (this.nezet === 'jogcim') this.sorok = await this.api('/jogcimek/' + this.aktiv.intezmeny);
      else if (this.nezet === 'munkaszam') { if (this.aktiv.ugyfelId) this.sorok = await this.api('/munkaszamok/' + this.aktiv.ugyfelId); }
      else this.sorok = await this.api('/nyugdijpenztarak/' + this.aktiv.intezmeny);
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült betölteni.'); }
  }

  uj() {
    this.szerk = this.nezet === 'jogcim' ? { intezmeny: this.aktiv.intezmeny, kod: '', nev: '', tipus: 'jovedelem', szja: true, jarulek: true, szocho: true, aktiv: true }
      : this.nezet === 'munkaszam' ? { ugyfel_id: this.aktiv.ugyfelId, kod: '', nev: '', aktiv: true }
      : { intezmeny: this.aktiv.intezmeny, nev: '', tipus: 'onkentes', adoszam: '', bankszamla: '', aktiv: true };
    this.szerkModal.present();
  }
  szerkeszt(r: any) {
    if (this.nezet === 'jogcim' && r.intezmeny === '*') return;
    this.szerk = { ...r, aktiv: !!r.aktiv, szja: !!r.szja, jarulek: !!r.jarulek, szocho: !!r.szocho,
      intezmeny: r.intezmeny === '*' ? this.aktiv.intezmeny : (r.intezmeny || this.aktiv.intezmeny), ugyfel_id: r.ugyfel_id || this.aktiv.ugyfelId };
    this.szerkModal.present();
  }
  async ment() {
    const ut = { jogcim: '/jogcim', munkaszam: '/munkaszam', nyugdijpenztar: '/nyugdijpenztar' }[this.nezet];
    try {
      await this.ber.torzsApi.post(ut, this.szerk);
      await this.szerkModal.dismiss(); await this.betolt(); this.ok('Mentve.');
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült menteni.'); }
  }
  async torol(r: any) {
    const a = await this.alert.create({ header: 'Törlés', message: `Biztosan törli: ${r.kod ? r.kod + ' ' : ''}${r.nev}?`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'destructive' }] });
    await a.present();
    if ((await a.onDidDismiss()).role !== 'destructive') return;
    const ut = { jogcim: '/jogcim/', munkaszam: '/munkaszam/', nyugdijpenztar: '/nyugdijpenztar/' }[this.nezet];
    try { await this.ber.torzsApi.del(ut + r.id); await this.betolt(); this.ok('Törölve.'); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
  }

  /*  A NYUGDÍJPÉNZTÁR-TÖRZS FRISSÍTÉSE AZ MNB-RŐL.
   *  Forrás: az MNB pénztári közzétételi oldala (kozzetetelek.mnb.hu). Onnan a pénztár neve és
   *  típusa jön; az adószámot, bankszámlát a frissítés nem bántja, és a kézzel felvett sorokhoz
   *  sem nyúl. Ami korábban innen jött, de kikerült a listáról, inaktív lesz (nem törlődik). */
  async mnbFrissites() {
    if (this.mnbFut) return;
    const a = await this.alert.create({
      header: 'Frissítés az MNB-ről',
      message: 'Lehozom az MNB közzétételi oldaláról a nyugdíjpénztárak aktuális listáját. '
        + 'A meglévő adatokat (adószám, bankszámla) nem írom felül, a kézzel felvett pénztárakat nem bántom.',
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Frissítés', role: 'ok' }],
    });
    await a.present();
    if ((await a.onDidDismiss()).role !== 'ok') return;
    this.mnbFut = true;
    try {
      const r: any = await this.ber.torzsApi.post(`/nyugdijpenztarak/${this.aktiv.intezmeny}/mnb`, {});
      await this.betolt();
      const reszek = [`${r.mnb} pénztár az MNB listáján`, `${r.uj} új`, `${r.frissitett} frissítve`];
      if (r.inaktivalt) reszek.push(`${r.inaktivalt} inaktiválva`);
      this.ok(reszek.join(' · '));
      if (!r.magan) this.figyelmeztet('Magánnyugdíjpénztár jelenleg nincs az MNB listáján – csak önkéntes pénztárak jöttek.');
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült frissíteni az MNB-ről.'); }
    finally { this.mnbFut = false; }
  }

  private async figyelmeztet(m: string) { (await this.toast.create({ message: m, duration: 4000, color: 'warning' })).present(); }
  private async ok(m: string) { (await this.toast.create({ message: m, duration: 1800, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 3200, color: 'danger' })).present(); }
}
